<section
  *ngIf="isOpen() && chat.available()"
  class="scb-panel"
  role="dialog"
  [attr.aria-label]="'support_chat.title' | translate"
  (dragenter)="onDragEnter($event)"
  (dragover)="onDragOver($event)"
  (dragleave)="onDragLeave()"
  (drop)="onDrop($event)"
>
  <input
    #filePicker
    type="file"
    class="scb-file-picker"
    multiple
    [accept]="fileAccept"
    (change)="onFilesPicked(filePicker)"
    tabindex="-1"
    aria-hidden="true"
  />
  <div class="scb-drop" *ngIf="dragging()">
    <mat-icon>upload_file</mat-icon>
    {{ "support_chat.drop_files" | translate }}
  </div>

  <div class="scb-banner" *ngIf="connectionLost()" role="status">
    {{ "support_chat.connection_lost" | translate }}
  </div>

  <!-- =============================== Home =============================== -->
  <div class="scb-screen scb-home" *ngIf="view() === 'home'">
    <header class="scb-hero">
      <div class="scb-hero-top">
        <img class="scb-hero-logo" [src]="logo" alt="" />
        <button
          type="button"
          class="scb-icon-btn scb-icon-btn--light"
          (click)="close()"
          [attr.aria-label]="'support_chat.close' | translate"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <p class="scb-hero-greeting">
        {{
          firstName
            ? ("support_chat.greeting" | translate: { name: firstName })
            : ("support_chat.greeting_anonymous" | translate)
        }}
        <span class="scb-wave" aria-hidden="true">👋</span>
      </p>
      <h2 class="scb-hero-title">
        {{ "support_chat.how_can_we_help" | translate }}
      </h2>
    </header>

    <div class="scb-home-body">
      <button type="button" class="scb-action-card" (click)="startMessage()">
        <span class="scb-action-text">
          <strong>{{ "support_chat.send_message" | translate }}</strong>
          <span>{{ "support_chat.send_message_hint" | translate }}</span>
        </span>
        <svg class="scb-action-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 4.5 20 12 5 19.5l2.2-7.5z" fill="currentColor" />
        </svg>
      </button>

      <ng-container *ngIf="latestConversation() as recent">
        <p class="scb-section-label">
          {{ "support_chat.recent_conversation" | translate }}
        </p>
        <ng-container
          *ngTemplateOutlet="
            conversationRow;
            context: { $implicit: recent, card: true }
          "
        ></ng-container>
      </ng-container>
    </div>
  </div>

  <!-- ============================= Messages ============================= -->
  <div class="scb-screen" *ngIf="view() === 'messages'">
    <header class="scb-header">
      <span class="scb-header-spacer"></span>
      <h2 class="scb-header-heading">
        {{ "support_chat.messages" | translate }}
      </h2>
      <button
        type="button"
        class="scb-icon-btn"
        (click)="close()"
        [attr.aria-label]="'support_chat.close' | translate"
      >
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <div class="scb-body scb-list-body">
      <div
        class="scb-loading"
        *ngIf="chat.loading() && !chat.conversations().length"
      >
        <span class="scb-dot"></span><span class="scb-dot"></span
        ><span class="scb-dot"></span>
      </div>

      <div
        class="scb-empty"
        *ngIf="!chat.loading() && !chat.conversations().length"
      >
        <svg class="scb-empty-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-3v3.5L13 17H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"
            fill="currentColor"
          />
          <path
            d="M7.5 8.5h9M7.5 12h5"
            stroke="var(--scb-surface)"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
        <h3>{{ "support_chat.no_conversations_title" | translate }}</h3>
        <p>{{ "support_chat.no_conversations_text" | translate }}</p>
      </div>

      <ng-container
        *ngFor="let conversation of chat.conversations(); trackBy: trackById"
      >
        <ng-container
          *ngTemplateOutlet="
            conversationRow;
            context: { $implicit: conversation, card: false }
          "
        ></ng-container>
      </ng-container>
    </div>

    <div class="scb-list-cta">
      <button type="button" class="scb-primary-btn" (click)="startMessage()">
        {{ "support_chat.send_message" | translate }}
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 4.5 20 12 5 19.5l2.2-7.5z" fill="currentColor" />
        </svg>
      </button>
    </div>
  </div>

  <!-- ============================== Thread ============================== -->
  <div class="scb-screen" *ngIf="view() === 'thread'">
    <header class="scb-header">
      <button
        type="button"
        class="scb-icon-btn"
        (click)="back()"
        [attr.aria-label]="'support_chat.back' | translate"
      >
        <mat-icon>chevron_left</mat-icon>
      </button>
      <div class="scb-header-title">
        <strong>
          {{
            (activeConversation()?.kind === "issue"
              ? "support_chat.issue_report"
              : "support_chat.support_team"
            ) | translate
          }}
        </strong>
        <span>
          {{
            activeConversation()?.assignedManagerName
              ? ("support_chat.assigned_to" | translate)
              : ("support_chat.typically_replies" | translate)
          }}
        </span>
      </div>
      <button
        type="button"
        class="scb-icon-btn"
        (click)="close()"
        [attr.aria-label]="'support_chat.close' | translate"
      >
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <div class="scb-thread" #messageList>
      <div
        class="scb-thread-intro"
        *ngIf="
          !activeConversation() ||
          (activeConversation()!.messagesLoaded &&
            !activeConversation()!.messages.length)
        "
      >
        <span class="scb-team-avatar" aria-hidden="true">
          <ng-container *ngTemplateOutlet="chatGlyph"></ng-container>
        </span>
        <strong>{{ "support_chat.support_team" | translate }}</strong>
        <p>{{ "support_chat.new_conversation_intro" | translate }}</p>
      </div>

      <ng-container *ngIf="activeConversation() as conversation">
        <div class="scb-loading" *ngIf="!conversation.messagesLoaded">
          <span class="scb-dot"></span><span class="scb-dot"></span
          ><span class="scb-dot"></span>
        </div>

        <button
          type="button"
          class="scb-link-btn scb-load-earlier"
          *ngIf="conversation.olderCursor"
          [disabled]="loadingOlder()"
          (click)="loadOlder()"
        >
          {{ "support_chat.load_earlier" | translate }}
        </button>

        <ng-container *ngIf="threadMessages() as messages">
          <div
            *ngFor="let message of messages; let i = index; trackBy: trackByClientId"
            class="scb-msg"
            [ngClass]="'scb-msg--' + message.author"
          >
            <span
              class="scb-msg-author"
              *ngIf="
                message.author === 'agent' &&
                (i === 0 || messages[i - 1].author !== 'agent')
              "
            >
              {{ "support_chat.support_team" | translate }}
            </span>

            <div class="scb-bubble" *ngIf="message.event?.type === 'assigned'">
              {{ "support_chat.agent_joined" | translate }}
            </div>
            <div class="scb-bubble" *ngIf="!message.event && message.body">{{
              message.body
            }}</div>

            <div class="scb-msg-files" *ngIf="message.attachments?.length">
              <ng-container *ngFor="let file of message.attachments">
                <button
                  *ngIf="isImage(file); else fileCard"
                  type="button"
                  class="scb-msg-image"
                  [title]="file.name"
                  (click)="openAttachment(message, file)"
                >
                  <img
                    *ngIf="imageSrc(file) as src; else imageLoading"
                    [src]="src"
                    [alt]="file.name"
                    (error)="onImageError(message, file)"
                  />
                  <ng-template #imageLoading>
                    <span class="scb-msg-image-placeholder">
                      <mat-icon>image</mat-icon>
                    </span>
                  </ng-template>
                </button>
                <ng-template #fileCard>
                  <button
                    type="button"
                    class="scb-file-card"
                    (click)="openAttachment(message, file)"
                  >
                    <mat-icon>{{ fileIcon(file.mimeType) }}</mat-icon>
                    <span class="scb-file-text">
                      <strong [title]="file.name">{{ file.name }}</strong>
                      <small>{{ fileSize(file.size) }}</small>
                    </span>
                  </button>
                </ng-template>
              </ng-container>
            </div>

            <div
              class="scb-request"
              *ngIf="message.request?.type === 'stop_nudges'"
            >
              <span>{{ "support_chat.stop_requested" | translate }}</span>
              <span class="scb-chip" *ngFor="let id of message.request!.nudgeIds">{{
                id
              }}</span>
            </div>

            <div class="scb-msg-meta" [ngSwitch]="message.status">
              <span *ngSwitchCase="'sending'">{{
                "support_chat.sending" | translate
              }}</span>
              <span *ngSwitchCase="'failed'" class="scb-text-error">
                {{ failureKey(message) | translate }}
                <button
                  type="button"
                  class="scb-link-btn"
                  *ngIf="message.error !== 'CONFLICT'"
                  (click)="retry(message)"
                >
                  {{ "support_chat.retry" | translate }}
                </button>
              </span>
              <ng-container *ngSwitchDefault>
                <span
                  *ngIf="message.author !== 'system' && isLastInGroup(messages, i)"
                >
                  {{
                    message.createdAt
                      | date: (isToday(message.createdAt) ? "shortTime" : "MMM d, HH:mm")
                  }}
                </span>
              </ng-container>
            </div>
          </div>

        </ng-container>

        <div class="scb-resolved" *ngIf="conversation.status === 'closed'">
          <mat-icon>check_circle</mat-icon>
          {{ "support_chat.conversation_closed" | translate }}
        </div>
      </ng-container>
    </div>

    <div class="scb-list-cta" *ngIf="activeConversation()?.status === 'closed'">
      <button type="button" class="scb-primary-btn" (click)="startMessage()">
        {{ "support_chat.start_new_conversation" | translate }}
      </button>
    </div>

    <p class="scb-file-notice" *ngIf="attachmentNotice() as notice" role="alert">
      {{ notice.key | translate: notice.params }}
    </p>
    <form
      class="scb-composer"
      *ngIf="activeConversation()?.status !== 'closed'"
      (ngSubmit)="send()"
    >
      <ng-container
        *ngTemplateOutlet="fileTray; context: { $implicit: composerFiles }"
      ></ng-container>
      <div class="scb-composer-row">
        <textarea
          #composer
          name="draft"
          rows="2"
          cdkTextareaAutosize
          cdkAutosizeMinRows="2"
          cdkAutosizeMaxRows="8"
          [(ngModel)]="draft"
          [maxlength]="maxLength"
          [placeholder]="'support_chat.composer_placeholder' | translate"
          (keydown)="onComposerKeydown($event)"
          (paste)="onPaste($event)"
        ></textarea>
        <button
          type="button"
          class="scb-attach-btn"
          *ngIf="canAttach()"
          (click)="pickFiles()"
          [title]="'support_chat.attach_file' | translate"
          [attr.aria-label]="'support_chat.attach_file' | translate"
        >
          <mat-icon>attach_file</mat-icon>
        </button>
        <button
          type="submit"
          class="scb-send-btn"
          [disabled]="!canSend"
          [attr.aria-label]="'support_chat.send' | translate"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 4.5 20 12 5 19.5l2.2-7.5z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </form>
  </div>

  <!-- ============================ Footer tabs ============================ -->
  <nav
    class="scb-tabs"
    *ngIf="view() === 'home' || view() === 'messages'"
  >
    <button
      type="button"
      class="scb-tab"
      [class.scb-tab--active]="view() === 'home'"
      [attr.aria-current]="view() === 'home' ? 'page' : null"
      (click)="setTab('home')"
    >
      <svg class="scb-tab-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path
          class="scb-tab-shape"
          d="M4 10.8 12 4.5l8 6.3V19a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
        />
      </svg>
      <span>{{ "support_chat.home" | translate }}</span>
    </button>
    <button
      type="button"
      class="scb-tab"
      [class.scb-tab--active]="view() === 'messages'"
      [attr.aria-current]="view() === 'messages' ? 'page' : null"
      (click)="setTab('messages')"
    >
      <span class="scb-tab-icon-wrap">
        <svg class="scb-tab-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            class="scb-tab-shape"
            d="M4 4.5h16a1 1 0 0 1 1 1V16a1 1 0 0 1-1 1h-3v3.5L13 17H4a1 1 0 0 1-1-1V5.5a1 1 0 0 1 1-1z"
          />
          <path class="scb-tab-detail" d="M7.5 9h9M7.5 12.5h5" />
        </svg>
        <span class="scb-badge" *ngIf="chat.unreadCount()">{{
          chat.unreadCount()
        }}</span>
      </span>
      <span>{{ "support_chat.messages" | translate }}</span>
    </button>
  </nav>
</section>

<button
  *ngIf="chat.available()"
  type="button"
  class="scb-launcher"
  [class.scb-launcher--open]="isOpen()"
  [class.scb-under-dialog]="reportOpen()"
  [attr.aria-expanded]="isOpen()"
  [attr.aria-label]="
    (isOpen() ? 'support_chat.close' : 'support_chat.open') | translate
  "
  (click)="toggle()"
>
  <span class="scb-launcher-icon scb-launcher-icon--chat">
    <ng-container *ngTemplateOutlet="chatGlyph"></ng-container>
  </span>
  <mat-icon class="scb-launcher-icon scb-launcher-icon--close"
    >expand_more</mat-icon
  >
  <span class="scb-badge scb-launcher-badge" *ngIf="!isOpen() && chat.unreadCount()">{{
    chat.unreadCount()
  }}</span>
</button>

<!-- "Report an issue": above the launcher, the label slides out on hover. -->
<button
  *ngIf="chat.available() && features.issueReports"
  type="button"
  class="scb-report-launcher"
  [class.scb-report-launcher--hidden]="isOpen()"
  [class.scb-under-dialog]="reportOpen()"
  [attr.tabindex]="isOpen() ? -1 : null"
  (click)="startReport()"
>
  <span class="scb-report-launcher-label">{{
    "support_chat.report_issue" | translate
  }}</span>
  <mat-icon>bug_report</mat-icon>
</button>

<!-- Shared conversation row (Messages list + Home "recent") -->
<ng-template #conversationRow let-conversation let-card="card">
  <button
    type="button"
    class="scb-conv"
    [class.scb-conv--card]="card"
    [class.scb-conv--unread]="conversation.unreadCount"
    (click)="openConversation(conversation)"
  >
    <span
      class="scb-conv-icon"
      [class.scb-conv-icon--issue]="conversation.kind === 'issue'"
      aria-hidden="true"
    >
      <mat-icon *ngIf="conversation.kind === 'issue'">bug_report</mat-icon>
      <ng-container *ngIf="conversation.kind !== 'issue'">
        <ng-container *ngTemplateOutlet="chatGlyph"></ng-container>
      </ng-container>
    </span>
    <span class="scb-conv-main">
      <span class="scb-conv-top">
        <strong>
          {{
            (conversation.kind === "issue"
              ? "support_chat.issue_report"
              : "support_chat.conversation"
            ) | translate
          }}
        </strong>
        <span class="scb-conv-time" *ngIf="conversation.preview as last">
          {{ last.at | date: (isToday(last.at) ? "shortTime" : "MMM d") }}
        </span>
      </span>
      <span class="scb-conv-preview" *ngIf="conversation.preview as last">
        <ng-container *ngIf="last.author === 'user'"
          >{{ "support_chat.you" | translate }}:
        </ng-container>
        {{ last.body || ("support_chat.attachment_only" | translate) }}
      </span>
      <span class="scb-conv-tags">
        <span class="scb-tag" *ngIf="hasSeveralTargets() && conversation.target">{{
          conversation.target
        }}</span>
        <span class="scb-tag" *ngIf="conversation.status === 'closed'">{{
          "support_chat.closed" | translate
        }}</span>
        <span
          class="scb-tag scb-tag--error"
          *ngIf="lastMessage(conversation)?.status === 'failed'"
          >{{ "support_chat.not_sent" | translate }}</span
        >
      </span>
    </span>
    <span class="scb-badge" *ngIf="conversation.unreadCount">{{
      conversation.unreadCount
    }}</span>
  </button>
</ng-template>

<!-- Files picked for the next message (chat composer and report form) -->
<ng-template #fileTray let-queue>
  <ul class="scb-tray" *ngIf="queue.count()">
    <li
      *ngFor="let item of queue.items()"
      class="scb-tray-item"
      [class.is-failed]="item.status === 'failed'"
    >
      <img
        *ngIf="item.previewUrl; else trayIcon"
        class="scb-tray-thumb"
        [src]="item.previewUrl"
        alt=""
        (error)="queue.dropPreview(item.localId)"
      />
      <ng-template #trayIcon>
        <mat-icon class="scb-tray-icon">{{ fileIcon(item.file.type) }}</mat-icon>
      </ng-template>
      <span class="scb-file-text">
        <strong [title]="item.file.name">{{ item.file.name }}</strong>
        <small *ngIf="item.status === 'failed'" class="scb-text-error">
          {{ uploadFailureKey(item) | translate }}
          <button
            type="button"
            class="scb-link-btn"
            (click)="queue.retry(item.localId)"
          >
            {{ "support_chat.retry" | translate }}
          </button>
        </small>
        <small *ngIf="item.status === 'uploading'"
          >{{ item.progress * 100 | number: "1.0-0" }}%</small
        >
        <small *ngIf="item.status === 'done'">{{ fileSize(item.file.size) }}</small>
      </span>
      <button
        type="button"
        class="scb-tray-remove"
        (click)="removeFile(queue, item.localId)"
        [attr.aria-label]="
          'support_chat.remove_file' | translate: { name: item.file.name }
        "
      >
        <mat-icon>close</mat-icon>
      </button>
      <span
        class="scb-tray-progress"
        *ngIf="item.status === 'uploading'"
        [style.width.%]="item.progress * 100"
      ></span>
    </li>
  </ul>
</ng-template>

<!-- Speech bubble: launcher, team avatar, message rows -->
<ng-template #chatGlyph>
  <svg class="scb-glyph" viewBox="0 0 32 32" aria-hidden="true">
    <path
      d="M7 4h18a4 4 0 0 1 4 4v13a4 4 0 0 1-4 4h-1v4.5L18.5 25H7a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z"
      fill="currentColor"
    />
  </svg>
</ng-template>
